/**
 * @file TanStack Query bindings for the Worker API.
 *
 * The client is built from the active auth session's token, so it is created
 * inside a hook rather than as a module singleton: a token belongs to a session,
 * and a module-scope client would have to cache one across sign-ins.
 *
 * `ME_QUERY_KEY` is the single source of truth for the shell: the workspaces a
 * user may touch. Creating a workspace writes the new row straight into that
 * cache instead of triggering a refetch, so the post-create render is
 * immediate — and the server stays authoritative because the next stale
 * refetch replaces it.
 */
import { useMemo } from "react";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type {
  ExpenseEntryCreateBody,
  GuarantorConsentBody,
  GuarantorContactCreateBody,
  LeaseCreateBody,
  LeaseStatus,
  LeaseTenantUpdateBody,
  LeaseUpdateBody,
  LedgerCorrectionCreateBody,
  MaintenanceEventCreateBody,
  MaintenanceRequestCreateBody,
  MaintenanceStatus,
  PaymentCreateBody,
  PropertyCreateBody,
  UnitCreateBody,
  WorkspaceSettingsUpdateBody,
  AssignableTeamRole,
} from "@neighboros/shared";
import { useWebEnv } from "../env-context";
import { createApiClient, type ApiClient, type ScheduleEReportParams } from "./client";
import type { ApiWorkspace, MeResponse } from "./types";

/** Query key for GET /me. Exported so tests and future writes can target it. */
export const ME_QUERY_KEY = ["me"] as const;

/** Query key for a workspace's property list. */
export function propertiesKey(workspaceId: string) {
  return ["properties", workspaceId] as const;
}

/** Query key for one property with its units. */
export function propertyKey(workspaceId: string, propertyId: string) {
  return ["property", workspaceId, propertyId] as const;
}

/** Query key for the dashboard aggregate. */
export function overviewKey(workspaceId: string) {
  return ["overview", workspaceId] as const;
}

/** Query key for the portfolio-wide unit list. */
export function unitsKey(workspaceId: string) {
  return ["units", workspaceId] as const;
}

/** Query key for the portfolio-wide lease list. */
export function leasesKey(workspaceId: string) {
  return ["leases", workspaceId] as const;
}

/** Query key for the portfolio-wide tenant list. */
export function tenantsKey(workspaceId: string) {
  return ["tenants", workspaceId] as const;
}

/**
 * Options shared by the three keyset-paged lists (ledger, payments, rent charges).
 *
 * The first page has no cursor; each later page's cursor is the previous page's
 * `nextCursor`, and `null` means there is no next page. The cache key is the same
 * as it was before paging, so every existing `invalidateQueries` on these keys
 * still works: it refetches every page that has been loaded.
 */
const pagedOptions = {
  initialPageParam: undefined as string | undefined,
  getNextPageParam: (last: { readonly nextCursor: string | null }) =>
    last.nextCursor ?? undefined,
};

/**
 * Query key for a workspace's ledger.
 *
 * Keyed apart from `leasesKey` on purpose: the ledger is a money view and the
 * leases screen a tenancy view, and one going stale must not blank the other.
 */
export function ledgerKey(workspaceId: string) {
  return ["ledger", workspaceId] as const;
}

/**
 * Query key for a workspace's rent charges.
 *
 * Keyed apart from `leasesKey` and `ledgerKey` on purpose: charges are the
 * OBLIGATIONS (a tenancy view of money), the ledger is what was POSTED, and one
 * going stale must not blank another.
 */
export function rentChargesKey(workspaceId: string) {
  return ["rent-charges", workspaceId] as const;
}

/**
 * Query key for a workspace's payment history (T1.5).
 *
 * Keyed apart from `ledgerKey` on purpose: the Ledger page is every posted row, this
 * is the money RECEIVED, and one going stale must not blank the other.
 */
export function paymentsKey(workspaceId: string) {
  return ["payments", workspaceId] as const;
}

/** Query key for the activity trail. */
export function activityKey(workspaceId: string) {
  return ["activity", workspaceId] as const;
}

/** GET /workspaces/:id/activity — the audit trail, newest first, keyset-paged. */
export function useActivity(workspaceId: string) {
  const api = useApiClient();
  return useInfiniteQuery({
    queryKey: activityKey(workspaceId),
    queryFn: ({ pageParam }) => api.listActivity(workspaceId, { cursor: pageParam }),
    ...pagedOptions,
  });
}

/**
 * Query key for the unit-owes / per-tenant view (T1.5).
 *
 * Separate from `paymentsKey` because they answer different questions — "what came
 * in" and "what is still owed" — and because a payment moves both.
 */
export function balancesKey(workspaceId: string) {
  return ["balances", workspaceId] as const;
}

/**
 * GET /workspaces/:id/balances — what each unit owes, and what each person paid.
 */
export function useBalances(workspaceId: string) {
  const api = useApiClient();
  return useQuery({
    queryKey: balancesKey(workspaceId),
    queryFn: () => api.listBalances(workspaceId),
  });
}

/**
 * Query key for a workspace's maintenance requests (T1.6).
 *
 * Keyed apart from every money key on purpose: a repair is neither an obligation
 * nor money, and one going stale must not blank another.
 */
export function maintenanceKey(workspaceId: string) {
  return ["maintenance", workspaceId] as const;
}

/**
 * GET /workspaces/:id/maintenance — every request, newest first.
 *
 * Read-only here: the §6.3 transition route exists and is proved by the API suite,
 * and a mutation hook with no caller would be the first half of a capability
 * nobody has built — the same rule the ledger and charge hooks follow. Logging a
 * request is `useLogMaintenanceRequest` below.
 */
export function useMaintenance(workspaceId: string) {
  const api = useApiClient();
  return useQuery({
    queryKey: maintenanceKey(workspaceId),
    queryFn: () => api.listMaintenance(workspaceId),
  });
}

/**
 * POST /workspaces/:id/maintenance.
 *
 * Invalidates the maintenance list rather than pushing the created row into the
 * cache: the server owns `status` (§6.3's `submitted` default), `createdAt` and
 * the ordering, so a locally synthesised row would be a guess the next read
 * contradicts. Nothing else is invalidated — a request moves no money and touches
 * no other screen's figures.
 */
export function useLogMaintenanceRequest(workspaceId: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: MaintenanceRequestCreateBody) =>
      api.createMaintenanceRequest(workspaceId, body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: maintenanceKey(workspaceId) });
    },
  });
}

/**
 * Query key for one request's own page.
 *
 * Carries the request id because the payload is per-request. Kept apart from
 * `maintenanceKey` on purpose: the work list and the request's own timeline are
 * different reads — a note moves one and reorders nothing in the other — and one going
 * stale must not blank the other. The prefix differs from the list's (`["maintenance",
 * workspaceId]`) so an id can never collide with a workspace id.
 */
export function maintenanceRequestKey(workspaceId: string, requestId: string) {
  return ["maintenance-request", workspaceId, requestId] as const;
}

/**
 * GET /workspaces/:id/maintenance/:requestId — one request and its timeline.
 *
 * `enabled` on a non-empty id: the route is entered with a param, and a fetch for the
 * empty string would be a 404 the page would have to explain.
 */
export function useMaintenanceRequest(workspaceId: string, requestId: string) {
  const api = useApiClient();
  return useQuery({
    queryKey: maintenanceRequestKey(workspaceId, requestId),
    queryFn: () => api.getMaintenanceRequest(workspaceId, requestId),
    enabled: requestId !== "",
  });
}

/**
 * POST /workspaces/:id/maintenance/:requestId/transitions — §6.3's one write path.
 *
 * Invalidates **three** keys, because one move genuinely changes three: the request's
 * own page (its status, its buttons and its timeline, which gained a
 * `status_transition` row), the portfolio work list (the status and its by-status
 * breakdown), and the dashboard — whose `attention.openMaintenance` count is folded
 * from exactly these statuses. Nothing here moves money, so no money key is touched.
 */
export function useTransitionMaintenanceRequest(workspaceId: string, requestId: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (to: MaintenanceStatus) =>
      api.transitionMaintenanceRequest(workspaceId, requestId, to),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: maintenanceRequestKey(workspaceId, requestId),
      });
      await queryClient.invalidateQueries({ queryKey: maintenanceKey(workspaceId) });
      await queryClient.invalidateQueries({ queryKey: overviewKey(workspaceId) });
    },
  });
}

/**
 * POST /workspaces/:id/maintenance/:requestId/events — append a note.
 *
 * A note is not a state move, so it invalidates the request's own timeline and nothing
 * else: no status changed, no list reordered, no figure moved. A note DOES move
 * `updated_at` on the request, which nothing on any screen renders.
 */
export function useAddMaintenanceNote(workspaceId: string, requestId: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: MaintenanceEventCreateBody) =>
      api.addMaintenanceNote(workspaceId, requestId, body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: maintenanceRequestKey(workspaceId, requestId),
      });
    },
  });
}

/**
 * GET /workspaces/:id/payments — every payment recorded, newest first.
 */
export function usePayments(workspaceId: string) {
  const api = useApiClient();
  return useInfiniteQuery({
    queryKey: paymentsKey(workspaceId),
    queryFn: ({ pageParam }) => api.listPayments(workspaceId, { cursor: pageParam }),
    ...pagedOptions,
  });
}

/**
 * POST /workspaces/:id/payments.
 *
 * Invalidates rather than patching the cache, and invalidates FIVE things, because
 * one payment genuinely moves all five: the payment history, the unit-owes view, the
 * §6.1 status of the charge it settled, the Ledger page (a payment IS a ledger row)
 * and the dashboard's money totals. The server owns every derived figure, so a
 * locally patched row would be a guess the next read contradicts.
 */
export function useRecordPayment(workspaceId: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: PaymentCreateBody) => api.recordPayment(workspaceId, body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: paymentsKey(workspaceId) });
      await queryClient.invalidateQueries({ queryKey: balancesKey(workspaceId) });
      await queryClient.invalidateQueries({ queryKey: rentChargesKey(workspaceId) });
      await queryClient.invalidateQueries({ queryKey: ledgerKey(workspaceId) });
      await queryClient.invalidateQueries({ queryKey: overviewKey(workspaceId) });
    },
  });
}

/**
 * GET /workspaces/:id/rent-charges — every charge, newest period first.
 *
 * Read-only: nothing on this screen writes a charge's §6.1 state. What it DOES
 * write is generation — `useGenerateRentCharges` below, the button that runs §6.4
 * on demand. The local stack has no cron, so without that button a fresh portfolio
 * could never produce a charge and therefore could never record a payment.
 */
export function useRentCharges(workspaceId: string) {
  const api = useApiClient();
  return useInfiniteQuery({
    queryKey: rentChargesKey(workspaceId),
    queryFn: ({ pageParam }) => api.listRentCharges(workspaceId, { cursor: pageParam }),
    ...pagedOptions,
  });
}

/**
 * POST /workspaces/:id/rent-charges/generate — the §6.4 run, on demand.
 *
 * Invalidates **four** keys, because one run genuinely moves all four:
 *
 *   - `rentChargesKey` — the rows the button just created.
 *   - `overviewKey` — the dashboard's money totals and its `overdueCharges` count
 *     are folded from charges.
 *   - `balancesKey` — the unit-owes view is charges minus what was applied.
 *   - `paymentsKey` — the payment form's "which charge does this settle" list is
 *     built from charges that are not yet settled, so a new charge is a new option.
 *
 * Deliberately NOT invalidated: `leasesKey` and `turnoverKey`. Generation reads
 * leases and writes none, so no lease field and no turnover classification changes
 * — a broad invalidation would suggest an effect that does not exist.
 *
 * The server owns every derived figure, so the response counts are reported to the
 * person and the rows are re-read rather than synthesised locally.
 */
export function useGenerateRentCharges(workspaceId: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.generateRentCharges(workspaceId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: rentChargesKey(workspaceId) });
      await queryClient.invalidateQueries({ queryKey: overviewKey(workspaceId) });
      await queryClient.invalidateQueries({ queryKey: balancesKey(workspaceId) });
      await queryClient.invalidateQueries({ queryKey: paymentsKey(workspaceId) });
    },
  });
}

/**
 * Query key for a workspace's expense list (T1.10).
 *
 * Keyed apart from every other money key on purpose: an expense is a cost against a
 * BUILDING (`expense_entries`), not a ledger row and not an obligation, and one
 * going stale must not blank another.
 */
export function expensesKey(workspaceId: string) {
  return ["expenses", workspaceId] as const;
}

/**
 * Query key for one building's P&L (T1.10).
 *
 * Carries the property id because the figure is per-building: two buildings have
 * two P&Ls, and one cache entry for both would show the wrong one after switching.
 */
export function propertyPnlKey(workspaceId: string, propertyId: string) {
  return ["property-pnl", workspaceId, propertyId] as const;
}

/** GET /workspaces/:id/expenses — the cost list, newest date first. */
export function useExpenses(workspaceId: string) {
  const api = useApiClient();
  return useQuery({
    queryKey: expensesKey(workspaceId),
    queryFn: () => api.listExpenses(workspaceId),
  });
}

/**
 * GET /workspaces/:id/properties/:propertyId/pnl — one building's P&L.
 *
 * `enabled` on a non-empty id: the screen shows the section only once a building is
 * chosen, and a fetch for the empty string would be a 404 the UI would have to
 * explain.
 */
export function usePropertyPnl(workspaceId: string, propertyId: string) {
  const api = useApiClient();
  return useQuery({
    queryKey: propertyPnlKey(workspaceId, propertyId),
    queryFn: () => api.getPropertyPnl(workspaceId, propertyId),
    enabled: propertyId !== "",
  });
}

/**
 * POST /workspaces/:id/expenses.
 *
 * Invalidates the list AND any cached P&L, because one expense genuinely moves
 * both: the totals in the header, the category breakdown, and the building's
 * expense side. The server owns every derived figure, so a locally patched row
 * would be a guess the next read contradicts.
 */
export function useCreateExpense(workspaceId: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: ExpenseEntryCreateBody) => api.createExpense(workspaceId, body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: expensesKey(workspaceId) });
      await queryClient.invalidateQueries({ queryKey: ["property-pnl", workspaceId] });
    },
  });
}

/**
 * DELETE /workspaces/:id/expenses/:expenseId.
 *
 * A hard delete, unlike the ledger's absent one: this table is not append-only and
 * the row is not a posting. A mistyped cost is noise a landlord must be able to
 * remove, and the history that matters — what a building cost — is what the
 * remaining rows say.
 */
export function useDeleteExpense(workspaceId: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (expenseId: string) => api.deleteExpense(workspaceId, expenseId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: expensesKey(workspaceId) });
      await queryClient.invalidateQueries({ queryKey: ["property-pnl", workspaceId] });
    },
  });
}

/**
 * GET /workspaces/:id/ledger — every ledger entry, newest first.
 *
 * Money is ENTERED on the Payments screen (T1.5) — `useRecordPayment` above —
 * because a payment must settle a named charge and move it in one transaction; this
 * hook is the RECORD, and the one write beside it (`useCorrectLedgerEntry` below)
 * never edits a row: it appends the replacement. The ledger genuinely holds money
 * received and nothing else, since rent charges are obligations that deliberately
 * post nothing: a portfolio with charges and no payments has an empty ledger, and
 * the page says so rather than inventing entries to fill it.
 */
export function useLedger(workspaceId: string) {
  const api = useApiClient();
  return useInfiniteQuery({
    queryKey: ledgerKey(workspaceId),
    queryFn: ({ pageParam }) => api.listLedger(workspaceId, { cursor: pageParam }),
    ...pagedOptions,
  });
}

/**
 * POST /workspaces/:id/ledger/:entryId/correction — the append-only way to fix an
 * entry.
 *
 * Invalidates everything a corrected amount genuinely moves, which is everything a
 * recorded payment moves (`useRecordPayment`: the payment history, the unit-owes
 * view, the charges, the Ledger page and the dashboard) PLUS the per-entity reads
 * that fold the same rows — a lease's own ledger and charges, a person's attributed
 * money, a building's P&L and the Schedule E summary. Those are keyed per id and this
 * hook knows only the entry, so the whole family for the workspace is invalidated.
 *
 * Invalidated on SETTLED rather than on success: the one refusal that matters —
 * 409, the entry was corrected by someone else first — means the rows on screen are
 * stale, and the honest response to "that row has been replaced" is to show the
 * replacement.
 */
export function useCorrectLedgerEntry(workspaceId: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { readonly entryId: string; readonly body: LedgerCorrectionCreateBody }) =>
      api.correctLedgerEntry(workspaceId, input.entryId, input.body),
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ledgerKey(workspaceId) });
      await queryClient.invalidateQueries({ queryKey: paymentsKey(workspaceId) });
      await queryClient.invalidateQueries({ queryKey: balancesKey(workspaceId) });
      await queryClient.invalidateQueries({ queryKey: rentChargesKey(workspaceId) });
      await queryClient.invalidateQueries({ queryKey: overviewKey(workspaceId) });
      await queryClient.invalidateQueries({ queryKey: ["lease-ledger", workspaceId] });
      await queryClient.invalidateQueries({ queryKey: ["lease-rent-charges", workspaceId] });
      await queryClient.invalidateQueries({ queryKey: ["tenant-detail", workspaceId] });
      await queryClient.invalidateQueries({ queryKey: ["property-pnl", workspaceId] });
      await queryClient.invalidateQueries({ queryKey: ["schedule-e-report", workspaceId] });
    },
  });
}

/**
 * GET /workspaces/:id/tenants — every person on a lease, with where they live.
 *
 * A read of rows the Leases screen also reads, keyed separately: the two pages
 * answer different questions and one going stale must not blank the other.
 */
export function useTenants(workspaceId: string) {
  const api = useApiClient();
  return useQuery({
    queryKey: tenantsKey(workspaceId),
    queryFn: () => api.listTenants(workspaceId),
  });
}

/**
 * Query key for one person's own page.
 *
 * Carries the tenant id because the payload is per-person. Kept apart from `tenantsKey`
 * on purpose, and the prefix is deliberately `tenant-detail` rather than `tenant`:
 * TanStack Query matches keys by PREFIX, so a singular `tenant` key would also be
 * invalidated by (and would invalidate) the plural list — and the two are different
 * reads, the list being the portfolio's answer to "who lives in my buildings" and this
 * one person's chain, with their unit, their property and the money attributed to them.
 * The names are similar; the reads are not.
 */
export function tenantDetailKey(workspaceId: string, tenantId: string) {
  return ["tenant-detail", workspaceId, tenantId] as const;
}

/**
 * GET /workspaces/:id/tenants/:tenantId — one person and the chain that places them.
 *
 * `enabled` on a non-empty id: the route is entered with a param, and a fetch for the
 * empty string would be a 404 the page would have to explain — the rule `useUnit` and
 * `useLease` follow.
 */
export function useTenant(workspaceId: string, tenantId: string) {
  const api = useApiClient();
  return useQuery({
    queryKey: tenantDetailKey(workspaceId, tenantId),
    queryFn: () => api.getTenant(workspaceId, tenantId),
    enabled: tenantId !== "",
  });
}

/**
 * PATCH /workspaces/:id/tenants/:tenantId — edit a person's contact and share details.
 *
 * Invalidates **three** keys, because one edit genuinely moves all three:
 *
 *   - `tenantDetailKey` — the person's own page, which renders what was just written.
 *   - `tenantsKey` — the portfolio list carries the same name, email, phone and share,
 *     and a row that kept the old phone number would be a stale copy of this record.
 *   - the `lease` family — a lease's tenant list NESTS these same fields, so correcting
 *     a name on the person changes what the agreement's own page shows.
 *
 * Deliberately NOT invalidated: `balancesKey`. The balance fields in the response are
 * derived from ledger rows this route does not write, so no figure moves.
 */
export function useUpdateTenant(workspaceId: string, tenantId: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: LeaseTenantUpdateBody) =>
      api.updateTenant(workspaceId, tenantId, body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: tenantDetailKey(workspaceId, tenantId),
      });
      await queryClient.invalidateQueries({ queryKey: tenantsKey(workspaceId) });
      await queryClient.invalidateQueries({ queryKey: ["lease", workspaceId] });
    },
  });
}

/** GET /workspaces/:id/leases — every lease, with its unit and its tenants. */
export function useLeases(workspaceId: string) {
  const api = useApiClient();
  return useQuery({
    queryKey: leasesKey(workspaceId),
    queryFn: () => api.listLeases(workspaceId),
  });
}

/**
 * Query key for one lease's own page.
 *
 * Carries the lease id because the payload is per-lease. Kept apart from `leasesKey`
 * on purpose: the list and the detail are different reads of the same rows, and one
 * going stale must not blank the other.
 */
export function leaseKey(workspaceId: string, leaseId: string) {
  return ["lease", workspaceId, leaseId] as const;
}

/** GET /workspaces/:id/leases/:leaseId — one lease and its tenants. */
export function useLease(workspaceId: string, leaseId: string) {
  const api = useApiClient();
  return useQuery({
    queryKey: leaseKey(workspaceId, leaseId),
    queryFn: () => api.getLease(workspaceId, leaseId),
    enabled: leaseId !== "",
  });
}

/** Query key for one lease's ledger and its balance. */
export function leaseLedgerKey(workspaceId: string, leaseId: string) {
  return ["lease-ledger", workspaceId, leaseId] as const;
}

/**
 * GET /workspaces/:id/leases/:leaseId/ledger — the balance and the entries.
 *
 * Its own key, apart from `leaseKey`, because the balance is a different question
 * from the lease's terms: a correction appended to the ledger moves this and not the
 * other.
 */
export function useLeaseLedger(workspaceId: string, leaseId: string) {
  const api = useApiClient();
  return useQuery({
    queryKey: leaseLedgerKey(workspaceId, leaseId),
    queryFn: () => api.getLeaseLedger(workspaceId, leaseId),
    enabled: leaseId !== "",
  });
}

/** Query key for one lease's rent charges. */
export function leaseRentChargesKey(workspaceId: string, leaseId: string) {
  return ["lease-rent-charges", workspaceId, leaseId] as const;
}

/**
 * GET /workspaces/:id/leases/:leaseId/rent-charges — what this lease owes.
 *
 * Apart from every other key for the same reason: an obligation is not money posted
 * and not the agreement, and one going stale must not blank another.
 */
export function useLeaseRentCharges(workspaceId: string, leaseId: string) {
  const api = useApiClient();
  return useQuery({
    queryKey: leaseRentChargesKey(workspaceId, leaseId),
    queryFn: () => api.getLeaseRentCharges(workspaceId, leaseId),
    enabled: leaseId !== "",
  });
}

/**
 * POST /workspaces/:id/leases — a lease and its tenants, in one transaction.
 *
 * Invalidates **five** keys, because signing a new agreement genuinely moves all
 * five and a stale dashboard after a lease is created is the bug this list exists
 * to prevent:
 *
 *   - `leasesKey` — the row that was just created.
 *   - `unitsKey` — a unit becomes leased; the unit list is where that shows.
 *   - `tenantsKey` — the people on the new lease are the people on the Tenants
 *     screen, folded from `lease_tenants` (the route writes them in the same
 *     transaction as the lease).
 *   - `overviewKey` — the dashboard's occupancy counts, `propertiesWithoutUnits`
 *     and every money figure are folded from leases and units.
 *   - `turnoverKey` — a unit's position between tenancies is classified from its
 *     leases, so a new draft on a unit changes which bucket it falls in.
 *
 * Deliberately NOT invalidated: `rentChargesKey`. A new lease is a `draft`, and
 * §6.4 generates charges for `active` and `month_to_month` leases only, so
 * generation would find nothing new to create until a transition makes it
 * chargeable — and `useTransitionLease` invalidates it then.
 *
 * The server owns `status` (the `draft` default), the audit row and every derived
 * figure, so the created lease is returned to the caller and the lists are re-read
 * rather than patched with a local guess.
 */
export function useCreateLease(workspaceId: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: LeaseCreateBody) => api.createLease(workspaceId, body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: leasesKey(workspaceId) });
      await queryClient.invalidateQueries({ queryKey: unitsKey(workspaceId) });
      await queryClient.invalidateQueries({ queryKey: tenantsKey(workspaceId) });
      await queryClient.invalidateQueries({ queryKey: overviewKey(workspaceId) });
      await queryClient.invalidateQueries({ queryKey: turnoverKey(workspaceId) });
      // Phase 2: a lease started FROM a unit's page changes that unit's lease list
      // and its "is anything in force here" answer. The unit family, since the
      // body names the unit but this hook's key is built from the id.
      await queryClient.invalidateQueries({ queryKey: ["unit", workspaceId] });
    },
  });
}

/**
 * POST /workspaces/:id/leases/:leaseId/transitions — §6.2's one write path.
 *
 * Invalidates **six portfolio-wide keys and three Phase-2 per-entity ones**, because
 * a status move moves more than the badge:
 *
 *   - `leasesKey` — the new state, and the buttons the next move may offer.
 *   - `turnoverKey` — `kind`, `inTransition` and §6.2's successor-guard verdict are
 *     all classified from lease STATUS, so `active → terminating` re-buckets a unit.
 *   - `overviewKey` — `unitsInTransition` and the money figures are folded from the
 *     same classification, and an `active` lease is what generation looks for.
 *   - `rentChargesKey` — activation is what makes a lease CHARGEABLE (§6.4 reads
 *     `active`/`month_to_month`), so charges may appear or need regenerating.
 *   - `unitsKey` — whether a unit is leased is read through its leases' states.
 *   - `tenantsKey` — the Tenants screen carries each person's `leaseStatus`.
 *
 * Phase 2 adds THREE more, all of them the same rows read per-lease rather than
 * portfolio-wide: `leaseKey` (the lease's own page shows the status and the buttons
 * the next move may offer), `leaseRentChargesKey` (activation is what makes a lease
 * CHARGEABLE, so its own charge list is stale) and `unitKey` (a unit page names
 * which lease is in force, which is a function of status alone). `leaseLedgerKey` is
 * deliberately NOT among them, for the same reason `balancesKey` is not: a status
 * move creates or removes no ledger row.
 *
 * Deliberately NOT invalidated: `balancesKey`. Balances are folded from charges and
 * applied money; a status move creates or removes no charge by itself, and the
 * generate button invalidates that key when it does.
 */
export function useTransitionLease(workspaceId: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { readonly leaseId: string; readonly to: LeaseStatus }) =>
      api.transitionLease(workspaceId, input.leaseId, input.to),
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({ queryKey: leasesKey(workspaceId) });
      await queryClient.invalidateQueries({ queryKey: turnoverKey(workspaceId) });
      await queryClient.invalidateQueries({ queryKey: overviewKey(workspaceId) });
      await queryClient.invalidateQueries({ queryKey: rentChargesKey(workspaceId) });
      await queryClient.invalidateQueries({ queryKey: unitsKey(workspaceId) });
      await queryClient.invalidateQueries({ queryKey: tenantsKey(workspaceId) });
      await queryClient.invalidateQueries({
        queryKey: leaseKey(workspaceId, variables.leaseId),
      });
      await queryClient.invalidateQueries({
        queryKey: leaseRentChargesKey(workspaceId, variables.leaseId),
      });
      // The unit page names which lease is in force, so it is stale after any move.
      // Its own key is per-unit and this mutation does not know the unit id, so the
      // whole `unit` family for this workspace is invalidated.
      await queryClient.invalidateQueries({ queryKey: ["unit", workspaceId] });
    },
  });
}

/**
 * PATCH /workspaces/:id/leases/:leaseId — edit a lease's own fields.
 *
 * The client's first caller is the lease PDF attachment (T0.8 Phase B). It invalidates
 * **two** keys, because attaching a signed agreement genuinely changes both reads: the
 * lease's own page (which renders the download link) and the portfolio list (whose rows
 * carry the same `pdfFileId`). `leaseLedgerKey`, `leaseRentChargesKey`, `unitsKey`,
 * `turnoverKey` and every money key are deliberately NOT invalidated — this PATCH
 * writes a file REFERENCE, and no tenancy, charge or balance moves because of one.
 *
 * Not a cache patch: the server owns the row and every derived field, so the queries
 * are re-read rather than the response written into the cache — the rule every
 * mutation hook in this file follows.
 */
export function useUpdateLease(workspaceId: string, leaseId: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: LeaseUpdateBody) => api.updateLease(workspaceId, leaseId, body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: leaseKey(workspaceId, leaseId) });
      await queryClient.invalidateQueries({ queryKey: leasesKey(workspaceId) });
    },
  });
}

/** GET /workspaces/:id/units — every unit, with its property. */
export function useUnits(workspaceId: string) {
  const api = useApiClient();
  return useQuery({
    queryKey: unitsKey(workspaceId),
    queryFn: () => api.listUnits(workspaceId),
  });
}

/**
 * Query key for one unit's own page.
 *
 * Carries the unit id because the payload is per-unit: two units have two chains,
 * two lease lists and two maintenance lists, and one cache entry for both would
 * show the wrong unit's tenancies after a navigation.
 */
export function unitKey(workspaceId: string, unitId: string) {
  return ["unit", workspaceId, unitId] as const;
}

/**
 * GET /workspaces/:id/units/:unitId — one unit, its property, its leases and its
 * maintenance, in one request.
 *
 * `enabled` on a non-empty id: the route is entered with a param, and a fetch for
 * the empty string would be a 404 the screen would have to explain.
 */
export function useUnit(workspaceId: string, unitId: string) {
  const api = useApiClient();
  return useQuery({
    queryKey: unitKey(workspaceId, unitId),
    queryFn: () => api.getUnit(workspaceId, unitId),
    enabled: unitId !== "",
  });
}

/** GET /workspaces/:id/overview — the dashboard. */
export function useOverview(workspaceId: string) {
  const api = useApiClient();
  return useQuery({
    queryKey: overviewKey(workspaceId),
    queryFn: () => api.overview(workspaceId),
  });
}

/**
 * Build the API client. It needs no session argument: the session is a first-party
 * cookie the browser attaches to every same-origin request (see ./client.ts).
 */
export function useApiClient(): ApiClient {
  const { apiBaseUrl } = useWebEnv();
  return useMemo(() => createApiClient({ baseUrl: apiBaseUrl }), [apiBaseUrl]);
}

/** GET /me — the signed-in user and their workspaces. */
export function useMe() {
  const api = useApiClient();
  return useQuery({
    queryKey: ME_QUERY_KEY,
    queryFn: () => api.me(),
  });
}

/**
 * POST /workspaces. On success the created workspace is appended to the
 * cached `me` payload, so callers can select it without waiting for a refetch.
 */
export function useCreateWorkspace() {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (name: string) => api.createWorkspace(name),
    onSuccess: (workspace: ApiWorkspace) => {
      queryClient.setQueryData<MeResponse>(ME_QUERY_KEY, (previous) => {
        if (previous === undefined) {
          // No cached payload yet (the create resolved before the first /me).
          // Returning undefined leaves the cache empty; the in-flight /me wins.
          return undefined;
        }
        return { ...previous, workspaces: [...previous.workspaces, workspace] };
      });
    },
  });
}

/** GET /workspaces/:id/properties */
export function useProperties(workspaceId: string) {
  const api = useApiClient();
  return useQuery({
    queryKey: propertiesKey(workspaceId),
    queryFn: () => api.listProperties(workspaceId),
  });
}

/**
 * Which IANA zone a property keeps its time in, looked up by `propertyId`.
 *
 * The ledger, payment, maintenance and lease-ledger payloads carry a `propertyId`
 * but not the property's `timezone`, so a screen that shows an INSTANT as a day
 * reads the zone from the properties list (already served, shared cache) instead
 * of falling back to the UTC day. The lookup returns `undefined` while the list
 * is loading or has failed, and for an unknown id; `formatInstantDate` then falls
 * back to the browser's zone, which is the only fallback available because there
 * is no workspace-level time zone (`timezone_default` is not built).
 */
export function usePropertyTimeZones(
  workspaceId: string,
): (propertyId: string | null | undefined) => string | undefined {
  const properties = useProperties(workspaceId);
  const rows = properties.data?.properties;
  return useMemo(() => {
    const zones = new Map<string, string>();
    for (const property of rows ?? []) zones.set(property.id, property.timezone);
    return (propertyId) => (propertyId == null ? undefined : zones.get(propertyId));
  }, [rows]);
}

/** GET /workspaces/:id/properties/:propertyId */
export function useProperty(workspaceId: string, propertyId: string) {
  const api = useApiClient();
  return useQuery({
    queryKey: propertyKey(workspaceId, propertyId),
    queryFn: () => api.getProperty(workspaceId, propertyId),
  });
}

/**
 * POST /workspaces/:id/properties.
 *
 * Invalidates the list rather than pushing the created row into the cache. The
 * server owns `country` (defaulted), `createdAt` ordering and any future
 * server-derived field, so a locally synthesised row would be a guess that the
 * next read contradicts. The list is small and on screen, so one refetch is the
 * honest trade — and `await`ing it is what makes the new row visible before the
 * form resets.
 *
 * Also invalidates `overviewKey` and `turnoverKey`: the dashboard's `counts`, its
 * `propertiesWithoutUnits` row and the onboarding ramp's first two steps are folded
 * from properties and units, and the turnover timeline classifies every unit — and
 * the form creates this building's units in the SAME request. A ramp card that did
 * not advance when a building is added would be the whole feature failing.
 */
export function useCreateProperty(workspaceId: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: PropertyCreateBody) => api.createProperty(workspaceId, body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: propertiesKey(workspaceId) });
      // The form creates units inline, so the portfolio-wide unit list is stale
      // even though this mutation never mentions units.
      await queryClient.invalidateQueries({ queryKey: unitsKey(workspaceId) });
      await queryClient.invalidateQueries({ queryKey: overviewKey(workspaceId) });
      // The ramp's `lease` step points at a UNIT, so a new building can change which
      // unit the dashboard offers to lease. Same rows, one more reader.
      await queryClient.invalidateQueries({ queryKey: turnoverKey(workspaceId) });
    },
  });
}

/**
 * POST /workspaces/:id/properties/:propertyId/units.
 *
 * The property's own page is invalidated because it lists the units; the
 * portfolio-wide list because it is the same rows.
 *
 * `overviewKey` and `turnoverKey` are here for the same reason as above: the new
 * unit is a `counts.units` row, the ramp's second step, a possible
 * `counts.unitsWithoutLease` and a new classified row on the Turnover page.
 * Nothing here moves money, so no money key is touched.
 */
export function useCreateUnit(workspaceId: string, propertyId: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: UnitCreateBody) => api.createUnit(workspaceId, propertyId, body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: propertyKey(workspaceId, propertyId) });
      await queryClient.invalidateQueries({ queryKey: unitsKey(workspaceId) });
      await queryClient.invalidateQueries({ queryKey: overviewKey(workspaceId) });
      await queryClient.invalidateQueries({ queryKey: turnoverKey(workspaceId) });
    },
  });
}

/** DELETE /workspaces/:id/properties/:propertyId — cascades to its units. */
export function useDeleteProperty(workspaceId: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (propertyId: string) => api.deleteProperty(workspaceId, propertyId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: propertiesKey(workspaceId) });
      // Delete cascades to units (migration 0003), so the unit list changes too.
      await queryClient.invalidateQueries({ queryKey: unitsKey(workspaceId) });
      // ...and so do the dashboard's property/unit counts, the ramp that reads them
      // and the turnover timeline that classified them.
      await queryClient.invalidateQueries({ queryKey: overviewKey(workspaceId) });
      await queryClient.invalidateQueries({ queryKey: turnoverKey(workspaceId) });
    },
  });
}

/**
 * Query key for a workspace's guarantor roster (T2.9).
 *
 * Keyed apart from `tenantsKey` on purpose: the Tenants page reads the PEOPLE ON A
 * LEASE (`lease_tenants`), while this reads the THIRD PARTIES who stand behind one
 * (`guarantor_contacts`). They are different nouns that both answer "who", and one
 * going stale must not blank the other.
 */
export function guarantorsKey(workspaceId: string) {
  return ["guarantors", workspaceId] as const;
}

/**
 * GET /workspaces/:id/guarantors — the roster, with counts folded server-side.
 *
 * Every row carries `consentState` and `mayContact`, computed on the server by the
 * shared pure functions, so this hook passes the guard's verdict through rather
 * than re-deriving it — one rule, one place.
 */
export function useGuarantors(workspaceId: string) {
  const api = useApiClient();
  return useQuery({
    queryKey: guarantorsKey(workspaceId),
    queryFn: () => api.listGuarantors(workspaceId),
  });
}

/**
 * POST /workspaces/:id/guarantors.
 *
 * Invalidates the roster rather than pushing the created row into the cache: the
 * server owns `consentState`, `mayContact`, the counts and the row's context, so a
 * locally synthesised row would be a guess the next read contradicts.
 */
export function useCreateGuarantor(workspaceId: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: GuarantorContactCreateBody) =>
      api.createGuarantor(workspaceId, body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: guarantorsKey(workspaceId) });
    },
  });
}

/**
 * POST /workspaces/:id/guarantors/:guarantorId/consent — the ONLY mutation that
 * flips `consent_recorded`.
 *
 * It invalidates the roster because one flip moves three figures at once — the
 * row's `consentState` and `mayContact`, and `counts.consented` — all of which the
 * server recomputes from the same rule.
 */
export function useRecordGuarantorConsent(workspaceId: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { readonly guarantorId: string; readonly body: GuarantorConsentBody }) =>
      api.recordGuarantorConsent(workspaceId, input.guarantorId, input.body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: guarantorsKey(workspaceId) });
    },
  });
}

/**
 * DELETE /workspaces/:id/guarantors/:guarantorId.
 *
 * A hard delete of an ordinary record. The one refusal — `409 guarantor_in_use`,
 * when a tenant on a lease names this guarantor — reaches the person through
 * `FormError` rather than being swallowed, because a delete that silently did
 * nothing would be worse than a sentence.
 */
export function useDeleteGuarantor(workspaceId: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (guarantorId: string) => api.deleteGuarantor(workspaceId, guarantorId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: guarantorsKey(workspaceId) });
    },
  });
}

/**
 * Query key for a workspace's turnover timeline (T2.10).
 *
 * Keyed apart from `leasesKey` on purpose: the Leases page reads the AGREEMENTS,
 * this reads the TRANSITION between two of them, and one going stale must not
 * blank the other.
 */
export function turnoverKey(workspaceId: string) {
  return ["turnover", workspaceId] as const;
}

/**
 * GET /workspaces/:id/turnover — every unit with its position between tenancies.
 *
 * Read-only by design, and there is deliberately no mutation hook beside it: this
 * page changes nothing. A repair that makes a unit ready is logged on the
 * Maintenance page (`useLogMaintenanceRequest`) and appears here through the unit
 * it names, which is also why this hook invalidates nothing.
 *
 * Everything the screen shows is computed by the server — `kind`, `inTransition`,
 * the day count in the building's zone, the gap, and §6.2's successor-guard verdict
 * (`leaseStatusAtEndOfTerm`). The screen renders those fields rather than
 * re-deriving them, so the header figure and the rows cannot disagree.
 */
export function useTurnover(workspaceId: string) {
  const api = useApiClient();
  return useQuery({
    queryKey: turnoverKey(workspaceId),
    queryFn: () => api.getTurnover(workspaceId),
  });
}

/**
 * Query key for the Schedule-E-shaped export's metadata (T2.11).
 *
 * The FILTERS are part of the key, because they are part of the answer: a March
 * range and a June range are two different summaries, and one cache entry for both
 * would show the wrong counts after a date change. The fields are primitives (empty
 * string for "no filter"), so the key is stable across renders even though the
 * screen builds a fresh params object each time.
 *
 * Keyed apart from every money key on purpose: this is neither an obligation nor
 * money received nor a cost — it is a description of a file.
 */
export function scheduleEReportKey(
  workspaceId: string,
  params: ScheduleEReportParams,
) {
  return [
    "schedule-e-report",
    workspaceId,
    params.from ?? "",
    params.to ?? "",
    params.propertyId ?? "",
  ] as const;
}

/**
 * GET /workspaces/:id/reports/schedule-e — what the export would contain.
 *
 * The counts are the server's, folded from the SAME projection the CSV is written
 * from, so the page's "12 rows" and the file's 12 data records are one claim. It
 * carries no money, and the page renders no total.
 *
 * The CSV itself is NOT a query: `api.downloadScheduleEReportCsv` returns a Blob
 * and is called by the download control, because a file must not be fetched on
 * every render and must not be parsed as JSON.
 */
export function useScheduleEReport(workspaceId: string, params: ScheduleEReportParams) {
  const api = useApiClient();
  return useQuery({
    queryKey: scheduleEReportKey(workspaceId, params),
    queryFn: () => api.getScheduleEReport(workspaceId, params),
  });
}

/**
 * Query key for a workspace's team roster.
 *
 * Keyed apart from `ME_QUERY_KEY` on purpose: `/me` answers "which workspaces may
 * I open" and this answers "who else is in this one". One going stale must not
 * blank the other, and the two are fetched on different screens.
 */
export function membersKey(workspaceId: string) {
  return ["members", workspaceId] as const;
}

/**
 * Query key for a workspace's settings.
 *
 * Keyed apart from `membersKey`: the roster and the settings are two different
 * questions on the same page, and a role change (not built) must not blank a
 * picker that has nothing to do with it.
 */
export function workspaceSettingsKey(workspaceId: string) {
  return ["workspace-settings", workspaceId] as const;
}

/**
 * GET /workspaces/:id/members — the team, read-only.
 *
 * The role breakdown, the counts and every `memberSince` come from the server, so
 * the header figure and the list are one fold rather than two. The owner-only
 * mutations (role, remove, rename) are the hooks that follow.
 */
export function useMembers(workspaceId: string) {
  const api = useApiClient();
  return useQuery({
    queryKey: membersKey(workspaceId),
    queryFn: () => api.listMembers(workspaceId),
  });
}

/** PATCH /workspaces/:id/members/:userId. Refetches the roster (and /me) whether it worked or not, so the page shows the server's truth. */
export function useUpdateMemberRole(workspaceId: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { readonly userId: string; readonly role: AssignableTeamRole }) =>
      api.updateMemberRole(workspaceId, input.userId, input.role),
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: membersKey(workspaceId) });
      // An owner may just have demoted themselves: their own role comes from /me.
      await queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
    },
  });
}

/** DELETE /workspaces/:id/members/:userId. */
export function useRemoveMember(workspaceId: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => api.removeMember(workspaceId, userId),
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: membersKey(workspaceId) });
      await queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
    },
  });
}

/** PATCH /workspaces/:id. The new name shows in /me (the sidebar) and the settings read. */
export function useRenameWorkspace(workspaceId: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => api.renameWorkspace(workspaceId, name),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
      await queryClient.invalidateQueries({ queryKey: workspaceSettingsKey(workspaceId) });
    },
  });
}

/**
 * GET /workspaces/:id/settings — the current proration method, and what the
 * platform supports.
 *
 * The supported methods arrive from the server (which reads `PRORATION_METHODS`),
 * so this hook passes them through rather than re-listing them — the rule the
 * turnover and guarantor hooks follow for anything derived from a kernel rule.
 */
export function useWorkspaceSettings(workspaceId: string) {
  const api = useApiClient();
  return useQuery({
    queryKey: workspaceSettingsKey(workspaceId),
    queryFn: () => api.getWorkspaceSettings(workspaceId),
  });
}

/**
 * PATCH /workspaces/:id/settings.
 *
 * Invalidates the settings query rather than patching the cache with the request
 * body: the server owns `notBuilt`, `options` and the read-back, so a locally
 * synthesised payload would be a guess the next read contradicts. It invalidates
 * **nothing else** — no money figure on any other screen changes when a
 * configuration changes, because the setting is read at charge-generation time and
 * existing charges are already priced. Saying that here is deliberate: a broad
 * invalidation would suggest an effect that does not exist.
 *
 * A 403 (a `member`) reaches the person through `FormError` rather than being
 * swallowed, because a picker that silently reverted would be worse than a
 * sentence.
 */
export function useUpdateWorkspaceSettings(workspaceId: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: WorkspaceSettingsUpdateBody) =>
      api.updateWorkspaceSettings(workspaceId, body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: workspaceSettingsKey(workspaceId) });
    },
  });
}

/** Query key for a workspace's pending invitations. Keyed apart from the roster: different question. */
export function invitationsKey(workspaceId: string) {
  return ["invitations", workspaceId] as const;
}

/** GET /workspaces/:id/invitations — pending invitations. Owner only: call it only for an owner. */
export function useInvitations(workspaceId: string) {
  const api = useApiClient();
  return useQuery({
    queryKey: invitationsKey(workspaceId),
    queryFn: () => api.listInvitations(workspaceId),
  });
}

/**
 * POST /workspaces/:id/invitations. Invalidates the pending list so the new row appears. The result
 * may carry a one-time `joinUrl` (only when no email could be sent); it lives in the mutation's own
 * state and is never written to the query cache.
 */
export function useCreateInvitation(workspaceId: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (email: string) => api.createInvitation(workspaceId, email),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: invitationsKey(workspaceId) });
    },
  });
}

/** DELETE /workspaces/:id/invitations/:invitationId — revoke, then refresh the pending list. */
export function useRevokeInvitation(workspaceId: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (invitationId: string) => api.revokeInvitation(workspaceId, invitationId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: invitationsKey(workspaceId) });
    },
  });
}

/**
 * POST /workspaces/invitations/accept. On success `/me` is invalidated so the new workspace appears
 * in the sidebar and the dashboard link resolves.
 */
export function useAcceptInvitation() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { readonly workspaceId: string; readonly token: string }) =>
      api.acceptInvitation(input.workspaceId, input.token),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
    },
  });
}
